import Tool, { type IToolActionInput, type IToolAvailableInput } from './Tool';
import DisableWhileUploadingBehavior from './DisableWhileUploadingBehavior';
import { isShape } from '../objects/guards';
import type ShapeImage from '../shapes/ShapeImage';

@DisableWhileUploadingBehavior
class ToolCopyImage extends Tool {
  static toolName = 'Copy image';

  static action({ selection }: IToolActionInput) {
    // @ts-expect-error assume we have the right type of object here due to checks below
    const shape = selection.shape as ShapeImage;

    const img = shape.image as HTMLImageElement;

    const fetchImage = async () => {
      const response = await fetch(img.currentSrc);
      const blob = await response.blob();
      return new Blob([blob], { type: 'image/png' });
    };

    /* Putting the promise chain in the ClipboardItem, rather than fetching data first, is a trick that works around a Safari error
    "NotAllowedError: The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission"
    This error would occur on writing to the clipboard even though Safari reported that `navigator.userActivation.isActive` was `true`.
    https://aha-labs-inc.sentry.io/issues/5098708461
    https://stackoverflow.com/a/77457920
    */
    const clipBoarditem = new ClipboardItem({
      'image/png': fetchImage(),
    });

    try {
      void navigator.clipboard.write([clipBoarditem]);
    } catch (e) {
      // the user might have focused outside the document, so try once more when it regains focus
      if (e.name === 'NotAllowedError') {
        console.warn(e);
        const writeOnFocus = () => {
          window.removeEventListener('focus', writeOnFocus);
          void navigator.clipboard.write([clipBoarditem]);
        };
        window.addEventListener('focus', writeOnFocus);
      } else {
        throw e;
      }
    }
  }

  static available({ selection }: IToolAvailableInput) {
    return !!(
      // Don't show if the browser doesn't support the Clipboard API.
      (
        typeof ClipboardItem !== 'undefined' &&
        selection &&
        isShape(selection) &&
        selection.shapeName === 'image' &&
        (selection.shape as ShapeImage).image
      )
    );
  }
}

export default ToolCopyImage;
